import { useState, useCallback, useEffect } from 'react';
import { adaptiveApi } from '../services/adaptiveApi';

/**
 * Hook for managing V2 Adaptive Learning status and toggle.
 *
 * @returns {{
 *   v2Enabled: boolean,
 *   isLoading: boolean,
 *   isToggling: boolean,
 *   error: string | null,
 *   toggleV2: (enable: boolean) => Promise<void>,
 *   refetch: () => Promise<void>
 * }}
 */
export function useAdaptiveV2() {
  const [v2Enabled, setV2Enabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adaptiveApi.getStatus({ auth: true });
      setV2Enabled(response.v2Enabled);
    } catch (err) {
      console.error('[useAdaptiveV2] Failed to fetch status:', err);
      setError(err?.message || 'Failed to fetch V2 status');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const toggleV2 = useCallback(async (enable) => {
    try {
      setIsToggling(true);
      setError(null);

      if (enable) {
        await adaptiveApi.enable({ auth: true });
      } else {
        await adaptiveApi.disable({ auth: true });
      }

      setV2Enabled(enable);
    } catch (err) {
      console.error('[useAdaptiveV2] Failed to toggle V2:', err);
      setError(err?.message || 'Failed to toggle V2');
      throw err;
    } finally {
      setIsToggling(false);
    }
  }, []);

  return {
    v2Enabled,
    isLoading,
    isToggling,
    error,
    toggleV2,
    refetch: fetchStatus,
  };
}
